import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Checkbox } from '../ui/checkbox';
import { Progress } from '../ui/progress';
import { 
  CreditCard, 
  DollarSign, 
  Loader2, 
  CheckCircle, 
  AlertCircle,
  ShoppingCart,
  User,
  Receipt,
  Lock,
  ArrowLeft,
  ArrowRight,
  Package,
  Crown,
  Calendar
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EnhancedCheckout = ({ cartData, onBack, onSuccess }) => {
  const { user } = useAuth();
  const { subscription } = useSubscription();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [billingInfo, setBillingInfo] = useState({
    firstName: user?.full_name?.split(' ')[0] || '',
    lastName: user?.full_name?.split(' ').slice(1).join(' ') || '',
    email: user?.email || '',
    phone: '',
    company: '',
    address: '',
    city: '',
    country: 'US',
    zipCode: ''
  });
  
  const [paymentInfo, setPaymentInfo] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    nameOnCard: ''
  });
  
  const [orderSummary, setOrderSummary] = useState(null);
  const [processingOrder, setProcessingOrder] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [agreesToTerms, setAgreesToTerms] = useState(false);
  const [currencyRate, setCurrencyRate] = useState(null);
  const [rateLoading, setRateLoading] = useState(false);
  
  // Calculate checkout summary
  const calculateOrderSummary = () => {
    if (!cartData) return null;
    
    const subscriptionItems = cartData.items?.filter(item => item.item_type === 'subscription') || [];
    const addOnItems = cartData.items?.filter(item => item.item_type === 'add_on') || [];
    
    return {
      subscriptionUpgrade: subscriptionItems.length > 0,
      subscriptionItems: subscriptionItems,
      addOnItems: addOnItems,
      subtotal: cartData.subtotal || 0,
      discountAmount: cartData.discount_amount || 0,
      taxAmount: cartData.tax_amount || 0,
      totalAmount: cartData.total_amount || 0,
      itemCount: cartData.items?.length || 0
    };
  };

  const summary = calculateOrderSummary();
  
  // Currency conversion for Chapa
  const currency = paymentMethod === 'chapa' ? 'ETB' : 'USD';
  const convertedTotal = paymentMethod === 'chapa' && currencyRate && summary
    ? Math.round(summary.totalAmount * currencyRate.rate * 100) / 100
    : summary?.totalAmount || 0;
  
  const steps = [
    { id: 1, name: 'Review Cart', icon: ShoppingCart },
    { id: 2, name: 'Billing Info', icon: User },
    { id: 3, name: 'Payment', icon: CreditCard },
    { id: 4, name: 'Confirmation', icon: CheckCircle }
  ];

  // Fetch currency rate when Chapa is selected
  useEffect(() => {
    if (paymentMethod === 'chapa') {
      fetchCurrencyRate();
    }
  }, [paymentMethod]);

  const fetchCurrencyRate = async () => {
    try {
      setRateLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/currency/usd-to-etb`);
      setCurrencyRate(response.data);
      console.log('✅ Currency rate loaded:', response.data);
    } catch (error) {
      console.error('❌ Error fetching currency rate:', error);
      // Fallback to approximate rate
      setCurrencyRate({
        rate: 120,
        description: '1 USD ≈ 120 ETB (estimated)',
        source: 'Fallback rate'
      });
    } finally {
      setRateLoading(false);
    }
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: paymentMethod === 'chapa' ? 'ETB' : 'USD'
    }).format(amount || 0);
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return summary && summary.itemCount > 0;
      case 2:
        return billingInfo.firstName && billingInfo.lastName && billingInfo.email;
      case 3:
        return paymentMethod && agreesToTerms;
      default:
        return true;
    }
  };

  const processCheckout = async () => {
    setProcessingOrder(true);
    try {
      console.log('🚀 Processing checkout...', {
        payment_method: paymentMethod,
        billing_info: billingInfo,
        cart_id: cartData.id,
        currency: currency,
        original_amount: summary?.totalAmount,
        converted_amount: convertedTotal
      });

      const checkoutData = {
        cart_id: cartData.id,
        billing_info: billingInfo,
        payment_method: paymentMethod,
        payment_info: paymentInfo,
        terms_accepted: agreesToTerms,
        currency: currency,
        converted_amount: convertedTotal,
        currency_rate: currencyRate?.rate
      };

      // Token is automatically added by axios defaults from AuthContext
      const response = await axios.post(`${BACKEND_URL}/api/cart/enhanced-checkout`, checkoutData);

      console.log('✅ Checkout response:', response.data);

      if (response.data) {
        // Check if Chapa redirect is required
        if (response.data.requires_redirect && response.data.checkout_url) {
          console.log('🔄 Redirecting to Chapa checkout...', response.data.checkout_url);
          // Save order ID for confirmation page
          localStorage.setItem('pending_order_id', response.data.order_id);
          // Redirect to Chapa checkout
          window.location.href = response.data.checkout_url;
          return;
        }

        // For Stripe or completed payments
        setOrderDetails(response.data);
        setOrderComplete(true);
        setCurrentStep(4);
        
        console.log('✅ Order completed successfully');
        
        // Call success callback
        if (onSuccess) {
          onSuccess(response.data);
        }
      }
    } catch (error) {
      console.error('❌ Checkout error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error occurred';
      alert('Checkout failed: ' + errorMessage);
    } finally {
      setProcessingOrder(false);
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep) && currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (orderComplete && orderDetails) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card className="text-center">
          <CardHeader>
            <div className="mx-auto mb-4 w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl text-green-600">Order Complete!</CardTitle>
            <CardDescription>
              Thank you for your purchase. Your order has been processed successfully.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="text-left">
                  <div className="text-gray-600">Order Number:</div>
                  <div className="font-semibold">{orderDetails.order_id}</div>
                </div>
                <div className="text-right">
                  <div className="text-gray-600">Total Amount:</div>
                  <div className="font-semibold text-lg">{formatPrice(orderDetails.total_amount)}</div>
                </div>
              </div>
            </div>
            
            <div className="space-y-2 text-sm text-gray-600">
              <p>• A confirmation email has been sent to {billingInfo.email}</p>
              <p>• Your services will be activated within 24 hours</p>
              <p>• You can track your order in the My Add-ons section</p>
            </div>
            
            <div className="flex gap-3 justify-center">
              <Button onClick={() => window.location.href = '/my-add-ons'}>
                <Package className="h-4 w-4 mr-2" />
                View My Services
              </Button>
              <Button variant="outline" onClick={() => window.location.href = '/dashboard'}>
                Back to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Progress Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Cart
          </Button>
          <div className="text-sm text-gray-600">
            Step {currentStep} of {steps.length}
          </div>
        </div>
        
        <div className="mt-4">
          <Progress value={(currentStep / steps.length) * 100} className="h-2" />
          <div className="flex justify-between mt-2">
            {steps.map((step) => (
              <div key={step.id} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep >= step.id ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {currentStep > step.id ? <CheckCircle className="h-4 w-4" /> : step.id}
                </div>
                <span className="ml-2 text-sm text-gray-600">{step.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2">
          {/* Step 1: Review Cart */}
          {currentStep === 1 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <ShoppingCart className="h-5 w-5 mr-2" />
                  Review Your Order
                </CardTitle>
                <CardDescription>
                  Please review your selected items before proceeding
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {summary?.subscriptionItems?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center">
                      <Crown className="h-4 w-4 mr-2" />
                      Subscription Changes
                    </h4>
                    {summary.subscriptionItems.map((item, index) => (
                      <div key={index} className="flex justify-between items-center p-3 bg-purple-50 rounded border">
                        <div>
                          <div className="font-medium">{item.name}</div>
                          <div className="text-sm text-gray-600">
                            {item.billing_cycle} billing
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold">{formatPrice(item.total_price)}</div>
                          <div className="text-sm text-gray-600">per {item.billing_cycle}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {summary?.addOnItems?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center">
                      <Package className="h-4 w-4 mr-2" />
                      Add-on Services ({summary.addOnItems.length})
                    </h4>
                    {summary.addOnItems.map((item, index) => (
                      <div key={index} className="flex justify-between items-center p-3 bg-blue-50 rounded border">
                        <div>
                          <div className="font-medium">{item.name}</div>
                          <div className="text-sm text-gray-600">
                            Quantity: {item.quantity}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold">{formatPrice(item.total_price)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Step 2: Billing Information */}
          {currentStep === 2 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <User className="h-5 w-5 mr-2" />
                  Billing Information
                </CardTitle>
                <CardDescription>
                  Enter your billing details for this order
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>First Name *</Label>
                    <Input
                      value={billingInfo.firstName}
                      onChange={(e) => setBillingInfo({...billingInfo, firstName: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Last Name *</Label>
                    <Input
                      value={billingInfo.lastName}
                      onChange={(e) => setBillingInfo({...billingInfo, lastName: e.target.value})}
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    value={billingInfo.email}
                    onChange={(e) => setBillingInfo({...billingInfo, email: e.target.value})}
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Phone Number</Label>
                    <Input
                      value={billingInfo.phone}
                      onChange={(e) => setBillingInfo({...billingInfo, phone: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Company (Optional)</Label>
                    <Input
                      value={billingInfo.company}
                      onChange={(e) => setBillingInfo({...billingInfo, company: e.target.value})}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Payment */}
          {currentStep === 3 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <CreditCard className="h-5 w-5 mr-2" />
                  Payment Method
                </CardTitle>
                <CardDescription>
                  Select your preferred payment method
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                  <div className="flex items-center space-x-2 p-3 border rounded hover:bg-gray-50">
                    <RadioGroupItem value="stripe" id="stripe" />
                    <Label htmlFor="stripe" className="flex items-center cursor-pointer flex-1">
                      <CreditCard className="h-4 w-4 mr-2" />
                      Credit/Debit Card (Stripe)
                      <Badge variant="outline" className="ml-auto">Recommended</Badge>
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-2 p-3 border rounded hover:bg-gray-50">
                    <RadioGroupItem value="chapa" id="chapa" />
                    <Label htmlFor="chapa" className="flex items-center cursor-pointer flex-1">
                      <DollarSign className="h-4 w-4 mr-2" />
                      Chapa Payment (ETB)
                      <Badge variant="secondary" className="ml-auto">Local</Badge>
                    </Label>
                  </div>
                </RadioGroup>
                
                <Separator />
                
                <div className="flex items-start space-x-2">
                  <Checkbox 
                    id="terms" 
                    checked={agreesToTerms}
                    onCheckedChange={setAgreesToTerms}
                  />
                  <Label htmlFor="terms" className="text-sm text-gray-600 cursor-pointer">
                    I agree to the <a href="/terms" className="text-blue-600 hover:underline">Terms of Service</a> and 
                    <a href="/privacy" className="text-blue-600 hover:underline"> Privacy Policy</a>
                  </Label>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Order Summary Sidebar */}
        <div className="lg:col-span-1">
          <Card className="sticky top-6">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Receipt className="h-5 w-5 mr-2" />
                Order Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {summary && (
                <>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Items ({summary.itemCount})</span>
                      <span>{formatPrice(summary.subtotal)}</span>
                    </div>
                    
                    {summary.discountAmount > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Discount</span>
                        <span>-{formatPrice(summary.discountAmount)}</span>
                      </div>
                    )}
                    
                    {summary.taxAmount > 0 && (
                      <div className="flex justify-between">
                        <span>Tax</span>
                        <span>{formatPrice(summary.taxAmount)}</span>
                      </div>
                    )}
                  </div>
                  
                  <Separator />
                  
                  <div className="flex justify-between font-semibold text-lg">
                    <span>Total</span>
                    <span>
                      {paymentMethod === 'chapa' && currencyRate ? (
                        <>
                          {currency} {convertedTotal.toFixed(2)}
                          <span className="text-xs text-gray-500 ml-1">
                            (${summary.totalAmount.toFixed(2)} USD)
                          </span>
                        </>
                      ) : (
                        formatPrice(summary.totalAmount)
                      )}
                    </span>
                  </div>
                  
                  {paymentMethod === 'chapa' && currencyRate && (
                    <div className="text-xs text-gray-600 mt-1">
                      Exchange Rate: {currencyRate.description}
                    </div>
                  )}
                </>
              )}
              
              <div className="space-y-2">
                {currentStep < 3 && (
                  <Button 
                    onClick={nextStep} 
                    className="w-full" 
                    disabled={!validateStep(currentStep)}
                  >
                    Continue
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                )}
                
                {currentStep === 3 && (
                  <Button 
                    onClick={processCheckout} 
                    className="w-full" 
                    disabled={!validateStep(currentStep) || processingOrder}
                  >
                    {processingOrder ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Lock className="h-4 w-4 mr-2" />
                        Complete Order
                      </>
                    )}
                  </Button>
                )}
                
                {currentStep > 1 && (
                  <Button onClick={prevStep} variant="outline" className="w-full">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Previous Step
                  </Button>
                )}
              </div>
              
              <div className="text-xs text-gray-500 text-center">
                <Lock className="h-3 w-3 inline mr-1" />
                Secure 256-bit SSL encryption
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EnhancedCheckout;